// Moteur de mesure pour navigateur (de préférence exécuté dans un Web Worker, voir worker.ts).
// Méthodologie : docs/methodologie.md. Implémentation jumelle en Go : internal/measure.
import { METHOD_VERSION, grade, isStable, jitter, mean, median, min, throughput } from './stats.js';
import { TestInvalidError } from './types.js';
const SAMPLE_MS = 100;
const r2 = (v) => (Number.isFinite(v) ? Math.round(v * 100) / 100 : null);
function summarize(xs, keep) {
    const l = { median_ms: r2(median(xs)), min_ms: r2(min(xs)), jitter_ms: r2(jitter(xs)) };
    if (keep)
        l.samples_ms = xs.map((x) => Math.round(x * 100) / 100);
    return l;
}
async function sha256Hex(s) {
    return new Uint8Array(await crypto.subtle.digest('SHA-256', new TextEncoder().encode(s)));
}
function leadingZeroBits(h) {
    let z = 0;
    for (const b of h) {
        if (b === 0) {
            z += 8;
            continue;
        }
        z += Math.clz32(b) - 24;
        break;
    }
    return z;
}
/** Résout un défi hashcash : SHA-256(défi + nonce) commence par `bits` bits nuls. */
export async function solvePoW(challenge, bits) {
    for (let i = 0;; i++) {
        if (leadingZeroBits(await sha256Hex(challenge + i)) >= bits)
            return String(i);
    }
}
class HTTPError extends Error {
    status;
    reason;
    constructor(status, reason, message) {
        super(message);
        this.status = status;
        this.reason = reason;
    }
}
async function postJSON(url, body, signal) {
    const resp = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
        signal,
        cache: 'no-store',
    });
    const data = await resp.json().catch(() => ({}));
    if (!resp.ok)
        throw new HTTPError(resp.status, data.reason ?? '', data.error ?? `HTTP ${resp.status}`);
    return data;
}
/** Données aléatoires réutilisées pour l'envoi : 1 Mio, assemblé en Blob sans copie. */
let randomChunk = null;
function randomBlob(size) {
    if (!randomChunk) {
        randomChunk = new Uint8Array(1 << 20);
        for (let i = 0; i < randomChunk.length; i += 65536)
            crypto.getRandomValues(randomChunk.subarray(i, i + 65536));
    }
    const parts = [];
    let left = size;
    while (left > 0) {
        const n = Math.min(left, randomChunk.length);
        parts.push(n === randomChunk.length ? randomChunk : randomChunk.subarray(0, n));
        left -= n;
    }
    return new Blob(parts, { type: 'application/octet-stream' });
}
/** Mesure de la latence HTTP vers un nœud (sélection du serveur) : minimum de 3 requêtes après préchauffage. */
export async function probeServer(base, signal) {
    const times = [];
    for (let i = 0; i < 4; i++) {
        const t = performance.now();
        const r = await fetch(base.replace(/\/$/, '') + '/health', { cache: 'no-store', signal });
        await r.arrayBuffer();
        if (!r.ok)
            throw new Error('nœud indisponible');
        if (i > 0)
            times.push(performance.now() - t);
    }
    return median(times);
}
/** Exécute un test complet. `onProgress` reçoit les mesures instantanées. */
export async function runTest(opts, onProgress = () => { }) {
    const o = {
        minDurationS: 10,
        maxDurationS: 15,
        minConns: 4,
        // Les navigateurs ouvrent au plus 6 connexions HTTP/1.1 par hôte : au-delà, les requêtes attendraient.
        maxConns: 6,
        pingCount: 20,
        ...opts,
    };
    const base = o.server.replace(/\/$/, '');
    const t0 = performance.now();
    const elapsed = () => (performance.now() - t0) / 1000;
    const warnings = [];
    const phases = (o.skipDownload ? 0 : 1) + (o.skipUpload ? 0 : 1);
    const totalS = 3 + phases * o.maxDurationS;
    const prog = (p, done) => onProgress({ ...p, t: elapsed(), progress: Math.min(1, done / totalS) });
    // Surveillance du client : un navigateur saturé (CPU) fausse la mesure.
    let lagEvents = 0;
    let lastTick = performance.now();
    const lagTimer = setInterval(() => {
        const now = performance.now();
        if (now - lastTick > 250 + 150)
            lagEvents++;
        lastTick = now;
    }, 250);
    try {
        prog({ phase: 'session' }, 0);
        let session;
        try {
            session = await postJSON(`${base}/v1/session`, {}, o.signal);
        }
        catch (e) {
            if (e instanceof HTTPError && e.reason === 'pow') {
                const ch = await (await fetch(`${base}/v1/challenge`, { cache: 'no-store', signal: o.signal })).json();
                const nonce = await solvePoW(ch.challenge, ch.bits);
                session = await postJSON(`${base}/v1/session`, { challenge: ch.challenge, nonce }, o.signal);
            }
            else
                throw e;
        }
        const wsURL = `${base.replace(/^http/, 'ws')}/v1/ping?session=${session.session}`;
        // 1. Latence au repos
        const idleRaw = await pingSeries(wsURL, o.pingCount, 0, o.signal, (rtt, i) => prog({ phase: 'latence', rtt_ms: rtt }, (3 * i) / o.pingCount));
        const idle = idleRaw.slice(1); // le premier échantillon inclut l'établissement de connexion
        const result = {
            server: base,
            node_id: session.node_id,
            method_version: METHOD_VERSION,
            started_at: new Date().toISOString(),
            idle_latency: summarize(idle, true),
            bufferbloat_grade: '?',
            warnings,
        };
        let offset = 3;
        for (const dir of ['descendant', 'montant']) {
            if ((dir === 'descendant' && o.skipDownload) || (dir === 'montant' && o.skipUpload))
                continue;
            const base0 = offset;
            const p = await phase(dir, base, session, wsURL, o, result.idle_latency.median_ms ?? NaN, (mbps, conns, t) => prog({ phase: dir, mbps, conns }, base0 + t));
            if (dir === 'descendant')
                result.download = p;
            else
                result.upload = p;
            offset += o.maxDurationS;
        }
        // 2. Clôture : reçu signé du nœud, qui fait foi pour le débit montant.
        const receipt = await postJSON(`${base}/v1/session/${session.session}/finish`, {
            client_download_mbps: result.download?.mbps ?? 0,
            client_upload_mbps: result.upload?.client_mbps ?? 0,
            upload_seconds: result.upload?.seconds ?? 0,
        }, o.signal);
        result.receipt = receipt;
        const rc = decodeReceipt(receipt);
        if (result.upload && rc) {
            if (rc.upload_mbps > 0)
                result.upload.mbps = rc.upload_mbps;
            if (!rc.upload_stable)
                result.upload.stable = false;
        }
        const loaded = [result.download, result.upload].map((p) => p?.loaded_latency.median_ms).filter((v) => typeof v === 'number');
        const idleMed = result.idle_latency.median_ms ?? NaN;
        result.bufferbloat_grade = grade(loaded.length ? Math.max(...loaded) - idleMed : NaN);
        if ([result.download, result.upload].some((p) => p && !p.stable))
            warnings.push('Débit instable : la phase stable est trop courte, résultat indicatif.');
        if (lagEvents >= 4) {
            result.invalid = 'client-lent';
            warnings.push("Votre appareil semble trop sollicité pour mesurer fidèlement ce débit : fermez d'autres applications.");
        }
        prog({ phase: 'fin' }, totalS);
        return result;
    }
    finally {
        clearInterval(lagTimer);
    }
}
function decodeReceipt(s) {
    try {
        // Le reçu est transporté en JSON brut (objet) ou en base64 selon la sérialisation Go de json.RawMessage.
        const r = typeof s.receipt === 'string' ? JSON.parse(atob(s.receipt)) : s.receipt;
        return r;
    }
    catch {
        return null;
    }
}
/** Envoie `n` pings WebSocket (ou jusqu'à l'arrêt si n = 0), espacés de `gapMs`. */
async function pingSeries(url, n, gapMs, signal, onRTT) {
    const ws = new WebSocket(url);
    const out = [];
    await new Promise((res, rej) => {
        ws.onopen = () => res();
        ws.onerror = () => rej(new Error('connexion WebSocket impossible'));
    });
    try {
        for (let i = 0; (n === 0 || i < n) && !signal?.aborted; i++) {
            const t = performance.now();
            const rtt = await new Promise((res, rej) => {
                const timer = setTimeout(() => rej(new Error('ping sans réponse')), 5000);
                ws.onmessage = () => {
                    clearTimeout(timer);
                    res(performance.now() - t);
                };
                ws.onclose = () => {
                    clearTimeout(timer);
                    rej(new TestInvalidError('connexion interrompue'));
                };
                ws.send(String(i));
            }).catch((e) => {
                if (n === 0)
                    return -1;
                throw e;
            });
            if (rtt < 0)
                break;
            out.push(rtt);
            onRTT?.(rtt, i + 1);
            await sleep(gapMs || 20, signal);
        }
    }
    finally {
        ws.close();
    }
    return out;
}
const sleep = (ms, signal) => new Promise((res) => {
    const t = setTimeout(res, ms);
    signal?.addEventListener('abort', () => (clearTimeout(t), res()), { once: true });
});
async function phase(dir, base, session, wsURL, o, idleMs, onSample) {
    const stop = new AbortController();
    const abortAll = () => stop.abort();
    o.signal?.addEventListener('abort', abortAll, { once: true });
    let total = 0;
    let chunk = 8 << 20;
    let conns = 0;
    let firstError = null;
    const workers = [];
    const worker = async () => {
        while (!stop.signal.aborted) {
            const size = Math.floor(Math.min(chunk, session.max_request_size));
            try {
                if (dir === 'descendant')
                    await downloadOnce(base, session.session, size, stop.signal, (n) => (total += n));
                else
                    await uploadOnce(base, session.session, size, stop.signal, (n) => (total += n));
            }
            catch (e) {
                if (stop.signal.aborted)
                    return;
                if (e instanceof HTTPError && e.reason === 'volume') {
                    stop.abort();
                    return;
                }
                firstError ??= e;
                await sleep(100);
            }
        }
    };
    const addConns = (k) => {
        for (let i = 0; i < k && conns < o.maxConns; i++) {
            conns++;
            workers.push(worker());
        }
    };
    // Latence sous charge en parallèle (après 300 ms). Si la file du goulot se remplit
    // (latence ≥ 2 × repos + 10 ms), le lien est plein : on n'ajoute plus de connexions.
    const live = [];
    const saturated = () => live.length >= 3 && !Number.isNaN(idleMs) && median(live.slice(-5)) >= 2 * idleMs + 10;
    const loadedP = sleep(300, stop.signal).then(() => stop.signal.aborted ? [] : pingSeries(wsURL, 0, 200, stop.signal, (r) => live.push(r)).catch(() => []));
    addConns(o.minConns);
    const samples = [];
    const t0 = performance.now();
    let last = 0;
    let lastT = t0;
    let perSec = 0;
    await new Promise((resolve) => {
        const timer = setInterval(() => {
            const now = performance.now();
            const mbps = ((total - last) * 8) / ((now - lastT) / 1000) / 1e6;
            samples.push(mbps);
            last = total;
            lastT = now;
            const el = (now - t0) / 1000;
            // Taille entière : ≈ 0,5 s de transfert par connexion, entre 1 et 256 Mio.
            chunk = Math.round(Math.max(1 << 20, Math.min(256 << 20, (((mbps * 1e6) / 8) * 0.5) / Math.max(1, conns))));
            onSample(Math.round(mbps * 100) / 100, conns, el);
            if (samples.length % 10 === 0 && el < 6) {
                const cur = mean(samples.slice(-10));
                if (perSec > 0 && cur > perSec * 1.1 && !saturated())
                    addConns(2);
                perSec = cur;
            }
            if (el >= o.maxDurationS || (el >= o.minDurationS && isStable(samples)) || stop.signal.aborted) {
                clearInterval(timer);
                resolve();
            }
        }, SAMPLE_MS);
    });
    const seconds = (performance.now() - t0) / 1000;
    stop.abort();
    o.signal?.removeEventListener('abort', abortAll);
    await Promise.allSettled(workers);
    const loaded = await loadedP;
    if (o.signal?.aborted)
        throw new TestInvalidError('test annulé');
    if (total === 0)
        throw firstError ?? new Error(`phase ${dir} : aucune donnée transférée`);
    const t = throughput(samples, SAMPLE_MS / 1000);
    const mbps = Math.round(t.mbps * 100) / 100;
    return {
        mbps,
        client_mbps: mbps,
        stable: t.ok,
        bytes: Math.round(total),
        seconds: Math.round(seconds * 100) / 100,
        conns,
        samples_mbps: samples.map((x) => Math.round(x * 10) / 10),
        loaded_latency: summarize(loaded, false),
    };
}
async function downloadOnce(base, session, size, signal, add) {
    const resp = await fetch(`${base}/v1/download?session=${session}&bytes=${Math.round(size)}`, {
        cache: 'no-store',
        signal,
    });
    if (!resp.ok) {
        const e = await resp.json().catch(() => ({}));
        throw new HTTPError(resp.status, e.reason ?? '', e.error ?? `HTTP ${resp.status}`);
    }
    const reader = resp.body.getReader();
    try {
        for (;;) {
            const { done, value } = await reader.read();
            if (done)
                return;
            add(value.byteLength);
        }
    }
    catch (e) {
        if (signal.aborted)
            return;
        throw e;
    }
}
function uploadOnce(base, session, size, signal, add) {
    // XMLHttpRequest (disponible dans les Web Workers) donne la progression de l'envoi,
    // ce que fetch ne permet pas de manière portable.
    return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        let sent = 0;
        xhr.open('POST', `${base}/v1/upload?session=${session}`);
        xhr.upload.onprogress = (e) => {
            add(e.loaded - sent);
            sent = e.loaded;
        };
        xhr.onload = () => {
            if (xhr.status === 200) {
                add(size - sent);
                resolve();
            }
            else {
                let e = {};
                try {
                    e = JSON.parse(xhr.responseText);
                }
                catch {
                    /* réponse non JSON */
                }
                reject(new HTTPError(xhr.status, e.reason ?? '', e.error ?? `HTTP ${xhr.status}`));
            }
        };
        xhr.onerror = () => reject(new Error("erreur réseau pendant l'envoi"));
        xhr.onabort = () => resolve();
        signal.addEventListener('abort', () => xhr.abort(), { once: true });
        xhr.send(randomBlob(size));
    });
}
